import {
  DefinedUseQueryResult,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { ImageSize } from '../../../types/isomorphic';
import useImageDownloadSizes from '../../shared/api/download/useImageDownloadSizesRequest';
import useDownloadModifiedAsset from '../useDownloadModifiedAsset';
import { getDownloadKey } from '../../shared/api/download/downloadModifiedQueries';

export const DOWNLOAD_SIZE_KEY = 'downloadSize';

const downloadSizeQueryInitialData: ImageSize = {
  sizeName: '',
  teeShirtSize: '',
  width: 0,
  height: 0,
  authorizationStatus: 'Authorized',
  fileType: 'Jpg',
};

export default function useDownloadSize(): {
  downloadSize: ImageSize;
  setDownloadSize: (size: ImageSize | undefined) => void;
} {
  const queryClient = useQueryClient();

  const { getDownloadSizes } = useImageDownloadSizes();
  // genId/index are typed nullable on the shared context (a plain, unmodified
  // asset has neither), but this hook is only used within the modified-download
  // flow, where both are always resolved by the time this hook runs.
  const { genId: genRequestId, index } = useDownloadModifiedAsset();

  const downloadSizesQuery = getDownloadSizes(
    genRequestId as string,
    index as number
  );

  const enabled = Boolean(downloadSizesQuery.data);

  const setDownloadSize = useCallback(
    (imageSize: ImageSize | undefined) => {
      const sizeKey = [DOWNLOAD_SIZE_KEY, genRequestId, index];
      const current = queryClient.getQueryData<ImageSize>(sizeKey);
      // A different size makes any completed download stale — drop it so the
      // next download click sends a fresh request instead of re-serving the
      // previously generated file from cache. imageSize can be undefined here:
      // LicensedModifiedSize's fibo path seeds this from /download-sizes and
      // calls in with `undefined` while that request is still in flight — an
      // undefined seed means the size is not known yet, not that a different
      // size was selected, so it must not invalidate a completed download.
      if (
        imageSize?.sizeName &&
        current?.sizeName &&
        current.sizeName !== imageSize.sizeName
      ) {
        queryClient.removeQueries({
          queryKey: getDownloadKey(genRequestId as string, index as number),
          exact: true,
        });
      }
      queryClient.setQueryData(sizeKey, imageSize);
    },
    [genRequestId, index, queryClient]
  );

  const downloadSizeQuery: DefinedUseQueryResult<ImageSize, unknown> = useQuery(
    {
      queryKey: [DOWNLOAD_SIZE_KEY, genRequestId, index],
      queryFn: ({ queryKey }) =>
        queryClient.getQueryData(queryKey) || downloadSizeQueryInitialData,
      enabled,
      initialData: () => downloadSizeQueryInitialData,
    }
  );

  return {
    downloadSize: downloadSizeQuery.data,
    setDownloadSize,
  };
}
